import { Button, Divider, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useMemo } from 'react';
import { PriceInputMasked } from '../../../../components/PriceMask';
import useErrors from '../../../../hooks/useErrors';
import { useFetch } from '../../../../hooks/useFecth';
import server from '../../../../services/server';
import { processAndSaveContractText } from '../../../../utils/processAndSaveContractText';

/**
 * Lista os additionals do catálogo (filtrados pelos tipos do plano).
 * Switch ligado cria/reativa a row; desligado só desabilita. Não apaga.
 */
export function AdditionalProducts({ mutate, contract }) {
    const { setErrors, enqueueSnackbar } = useErrors();

    const { data: catalogRaw } = useFetch('additional?paged=false&status=true&type=additional');

    const planTypes = useMemo(() => {
        const fromPlan = contract?.plan?.additionals;
        const fromData = contract?.contractData?.additionals;
        const list = Array.isArray(fromPlan) ? fromPlan : Array.isArray(fromData) ? fromData : [];
        return list.filter(Boolean);
    }, [contract?.plan?.additionals, contract?.contractData?.additionals]);

    const catalog = useMemo(() => {
        const rows = Array.isArray(catalogRaw) ? catalogRaw : catalogRaw?.data || [];
        if (!planTypes.length) return rows;
        return rows.filter(p => planTypes.includes(p.additionalType));
    }, [catalogRaw, planTypes]);

    const activeByProductId = useMemo(() => {
        const map = new Map();
        for (const r of contract?.resaleProducts || []) {
            if (r.productId && !r.deletedAt && !r.disabled) {
                map.set(Number(r.productId), r);
            }
        }
        return map;
    }, [contract?.resaleProducts]);

    const { values, setFieldValue, setValues, isSubmitting, handleSubmit, getFieldProps } = useFormik({
        initialValues: { items: [] },
        onSubmit: async (formValues, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const items = (formValues.items || []).map(item => ({
                    productId: Number(item.productId),
                    name: item.name,
                    type: item.additionalType || item.type,
                    additionalType: item.additionalType || item.type,
                    price: Number(item.price),
                    numberDependents: Number(item.numberDependents ?? 0),
                    enabled: Boolean(item.enabled),
                }));

                await server.put(`subscription/${contract.uuid}/resale-products`, { items });
                await mutate?.();
                await processAndSaveContractText(contract.uuid, { mutate });
                enqueueSnackbar('Benefícios adicionais atualizados');
            } catch (error) {
                setErrors(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    useEffect(() => {
        const items = catalog.map(product => {
            const resale = activeByProductId.get(Number(product.id));
            return {
                productId: product.id,
                name: product.name,
                additionalType: product.additionalType,
                type: product.additionalType || product.type,
                numberDependents: product.numberDependents ?? 0,
                price: resale ? Number(resale.price) : Number(product.price ?? 0),
                enabled: Boolean(resale),
                resaleId: resale?.id,
            };
        });
        setValues({ items });
    }, [catalog, activeByProductId, setValues]);

    if (!catalog.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                Nenhum benefício adicional disponível para este plano.
            </Typography>
        );
    }

    return (
        <Stack spacing={2}>
            {values.items.map((item, k) => (
                <Stack
                    direction="row"
                    spacing={2}
                    key={item.productId}
                    sx={{ opacity: item.enabled ? 1 : 0.55 }}
                    alignItems="center"
                >
                    <TextField
                        label="Nome"
                        fullWidth
                        value={item.name}
                        InputProps={{ readOnly: true }}
                    />
                    <TextField
                        label="Valor"
                        fullWidth
                        value={Number(item.price)}
                        {...getFieldProps(`items[${k}].price`)}
                        disabled={!item.enabled || isSubmitting}
                        InputProps={{ inputComponent: PriceInputMasked }}
                    />
                    <FormControlLabel
                        sx={{ mx: 0, minWidth: 88 }}
                        control={
                            <Switch
                                checked={Boolean(item.enabled)}
                                disabled={isSubmitting}
                                onChange={e => setFieldValue(`items[${k}].enabled`, e.target.checked)}
                                color="primary"
                            />
                        }
                        label={item.enabled ? 'Ativo' : 'Inativo'}
                        labelPlacement="start"
                    />
                </Stack>
            ))}

            <Divider />
            <Button
                sx={{ alignSelf: 'flex-end' }}
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
            >
                Salvar
            </Button>
        </Stack>
    );
}
